const toFiniteNumber = (value) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : null;
};

const roundCurrency = (value) => Math.max(0, Math.round(Number(value) || 0));

const hasOwn = (value, key) => Object.prototype.hasOwnProperty.call(value || {}, key);

export const sanitizeDiscount = (value) => {
  const numericValue = toFiniteNumber(value);

  if (numericValue === null) {
    return 0;
  }

  return Math.min(100, Math.max(0, Math.round(numericValue * 100) / 100));
};

export const resolveBasePrice = (product = {}) => {
  const basePriceCandidate =
    toFiniteNumber(product?.basePrice) ??
    toFiniteNumber(product?.originalPrice) ??
    toFiniteNumber(product?.price) ??
    toFiniteNumber(product?.salePrice);

  return roundCurrency(basePriceCandidate || 0);
};

export const resolveSalePrice = (product = {}) => {
  const basePrice = resolveBasePrice(product);
  const discount = sanitizeDiscount(product?.discount);

  if (discount > 0) {
    return roundCurrency(basePrice * (1 - discount / 100));
  }

  const explicitSalePrice = toFiniteNumber(product?.salePrice) ?? toFiniteNumber(product?.price);
  return roundCurrency(Math.min(basePrice, explicitSalePrice ?? basePrice));
};

export const getProductPricing = (product = {}) => {
  const regularPrice = resolveBasePrice(product);
  const salePrice = resolveSalePrice(product);
  const hasDiscount = regularPrice > salePrice;
  const configuredDiscount = sanitizeDiscount(product?.discount);
  const discount = hasDiscount
    ? configuredDiscount ||
      Math.round(((regularPrice - salePrice) / regularPrice) * 10000) / 100
    : 0;

  return {
    regularPrice,
    salePrice,
    discount,
    hasDiscount,
  };
};

export const normalizeProductPricing = (product = {}) => {
  const { regularPrice: basePrice, salePrice, discount } = getProductPricing({
    ...product,
  });

  return {
    ...product,
    discount,
    basePrice,
    originalPrice: basePrice,
    salePrice,
    price: salePrice,
  };
};

export const hasDiscountedPrice = (product = {}) =>
  resolveBasePrice(product) > resolveSalePrice(product);

export const prepareProductPricingForPersistence = (product = {}) => {
  const {
    basePrice: _basePrice,
    originalPrice: _originalPrice,
    salePrice: _salePrice,
    ...rest
  } = product;

  const hasBasePriceKeys = ['price', 'basePrice', 'originalPrice', 'salePrice'].some((key) =>
    hasOwn(product, key)
  );
  const hasDiscountKey = hasOwn(product, 'discount');

  if (!hasBasePriceKeys && !hasDiscountKey) {
    return rest;
  }

  return {
    ...rest,
    ...(hasBasePriceKeys ? { price: resolveBasePrice(product) } : {}),
    ...(hasDiscountKey ? { discount: sanitizeDiscount(product?.discount) } : {}),
  };
};
